import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PAYMENT_METHOD, PLATFORM_CATALOG } from '@/@types/Checkout.type';
import { CREDITS_BALANCE_QUERY_KEY } from '@/hooks/useCreditsBalance';
import { BOOTSTRAP_QUERY_KEYS } from '@/hooks/useBootstrapQueries';
import useEvents from '@/hooks/useEvents';
import { postShoppingCartProcess } from '@/services/sales.service';
import { getUtmsForCheckout } from '@/shared/attribution/utm-manager';
import { resolveDeclineMessage } from '@/shared/checkout/declineMessage';
import { getStripe } from '@/shared/checkout/stripeClient';
import { EVENT_TYPES } from '@/shared/constants/events';
import { getIsNative } from '@/shared/utils/general.util';
import { planChangeRefusalOf, type PlanChangeRefusal } from '../lib/planChangeErrors';
import type { PlanChangeRequest, PlanChangeResult, PlanChangeTiming } from '../lib/types';
import {
	listPlanChanges,
	quotePlanChange,
	requestPlanChange,
} from '../services/planChange.service';
import { PLAN_CHANGE_HISTORY_QUERY_KEY } from './usePlanChangeAvailability';

export type PlanChangeStep = 'review' | 'processing' | 'done' | 'refused';

/** `paid_pending`: the card was charged and the engine has not applied the change yet. */
export type PlanChangeDone = PlanChangeResult & { settled: 'applied' | 'scheduled' | 'paid_pending' };

const APPLY_POLL_INTERVAL_MS = 1500;
const APPLY_POLL_TIMEOUT_MS = 30000;

const declineRefusal = (code?: string | null, declineCode?: string | null): PlanChangeRefusal => ({
	code: declineCode || code || 'card_declined',
	title: 'No pudimos cobrar tu tarjeta',
	description: resolveDeclineMessage(code, declineCode, { cardOnly: true }),
	next: 'retry',
});

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * One plan change, from the quote to the result, for `PlanChangeModal`.
 *
 * Contract:
 * - The quote is the engine's; the web renders it and sends back its
 *   `diff_cents`, so a quote that moved meanwhile is refused (409), never charged.
 * - An upgrade with a charge pays its one-line adjustment cart with a card
 *   (processv2, then Stripe.js for 3-D Secure). The engine applies the change in
 *   the payment's webhook; this hook only waits for it to show the result.
 * - A failed or abandoned payment keeps the pending change: a retry pays the
 *   same cart again, it never asks for a second change.
 * - Double taps are absorbed here (one call in flight); two tabs are settled by
 *   the engine (the newer change supersedes the older and closes its cart).
 */
export const usePlanChangeFlow = (request: PlanChangeRequest, onChanged?: () => void) => {
	const queryClient = useQueryClient();
	const { trackEvent } = useEvents();
	const { subscriptionId, target, entry } = request;
	const [step, setStep] = useState<PlanChangeStep>('review');
	const [refusal, setRefusal] = useState<PlanChangeRefusal | null>(null);
	const [done, setDone] = useState<PlanChangeDone | null>(null);
	const pendingChange = useRef<PlanChangeResult | null>(null);
	const busy = useRef(false);

	const quoteQuery = useQuery({
		queryKey: ['plan-change', 'quote', subscriptionId, target.productVersionId],
		queryFn: async () => {
			const quote = await quotePlanChange(subscriptionId, target.productVersionId);
			trackEvent({
				eventName: EVENT_TYPES.PLAN_CHANGE_QUOTE_VIEWED,
				eventPayload: {
					entry,
					subscription_id: subscriptionId,
					from_product_version_id: quote.from.product_version_id,
					to_product_version_id: quote.to.product_version_id,
					direction: quote.direction,
					outcome: quote.outcome,
					periodicity_change: quote.periodicity_change ?? 'none',
					diff_cents: quote.diff_cents,
				},
			});
			return quote;
		},
		retry: false,
		staleTime: 0,
		gcTime: 0,
		refetchOnWindowFocus: false,
	});
	const quote = quoteQuery.data ?? null;
	const quoteRefusal = quoteQuery.isError ? planChangeRefusalOf(quoteQuery.error) : null;

	const refuse = (next: PlanChangeRefusal, stage: 'confirm' | 'payment') => {
		trackEvent({
			eventName: EVENT_TYPES.PLAN_CHANGE_FAILED,
			eventPayload: {
				entry,
				stage,
				subscription_id: subscriptionId,
				plan_change_id: pendingChange.current?.plan_change_id ?? null,
				error_code: next.code,
			},
		});
		if (next.next === 'requote') pendingChange.current = null;
		setRefusal(next);
		setStep('refused');
	};

	const finish = (change: PlanChangeResult, settled: PlanChangeDone['settled'], timing: PlanChangeTiming) => {
		pendingChange.current = null;
		trackEvent({
			eventName: EVENT_TYPES.PLAN_CHANGE_CONFIRMED,
			eventPayload: {
				entry,
				subscription_id: subscriptionId,
				plan_change_id: change.plan_change_id,
				outcome: change.outcome,
				periodicity_change: change.periodicity_change ?? 'none',
				timing,
				settled,
				charge_cents: change.charge_cents,
				heru_cash_pesos: change.heru_cash_pesos,
			},
		});
		queryClient.invalidateQueries({ queryKey: BOOTSTRAP_QUERY_KEYS.salesOverview });
		queryClient.invalidateQueries({ queryKey: PLAN_CHANGE_HISTORY_QUERY_KEY(subscriptionId) });
		queryClient.invalidateQueries({ queryKey: CREDITS_BALANCE_QUERY_KEY });
		setDone({ ...change, settled });
		setStep('done');
		onChanged?.();
	};

	const waitUntilApplied = async (planChangeId: number) => {
		const deadline = Date.now() + APPLY_POLL_TIMEOUT_MS;
		while (Date.now() < deadline) {
			await sleep(APPLY_POLL_INTERVAL_MS);
			try {
				const changes = await listPlanChanges(subscriptionId);
				if (changes.find((change) => change.id === planChangeId)?.status === 'applied') return true;
			} catch {
				return false;
			}
		}
		return false;
	};

	/** Charges the adjustment cart. Returns a refusal, or null when the card was charged. */
	const payAdjustment = async (change: PlanChangeResult, tokenId: string): Promise<PlanChangeRefusal | null> => {
		const response = await postShoppingCartProcess({
			cartId: change.proration_cart_id as number,
			tokenId,
			renewSubscription: false,
			additionalInformation: { utms: getUtmsForCheckout() },
			sourceChannel: getIsNative() ? 'app' : 'web',
			paymentMethod: PAYMENT_METHOD.CARD,
			platformId: PLATFORM_CATALOG.STRIPE,
		});
		if (!response?.status) return planChangeRefusalOf({ response: { data: response } });
		if (response.status === 'processed') return null;
		const clientSecret = response.transaction?.additional_information?.cliente_secret;
		if (!clientSecret) {
			return declineRefusal(response.transaction?.error_code, null);
		}
		const stripe = await getStripe();
		if (!stripe) return declineRefusal('processing_error', null);
		const { error, paymentIntent } = await stripe.confirmPayment({
			clientSecret,
			confirmParams: { return_url: window.location.href },
			redirect: 'if_required',
		});
		if (error) return declineRefusal(error.code, error.decline_code);
		if (paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'processing') return null;
		return declineRefusal(paymentIntent?.last_payment_error?.code, paymentIntent?.last_payment_error?.decline_code);
	};

	/**
	 * Confirms the quote. `tokenId` is the card for an upgrade with a charge;
	 * `timing: 'renewal'` asks for a new, scheduled change (no heru-cash, no
	 * charge today) and never reuses the pending charge of a declined or
	 * abandoned payment: the engine supersedes it and closes its cart.
	 */
	const confirm = async ({
		tokenId,
		timing = 'now',
	}: {
		tokenId?: string;
		timing?: PlanChangeTiming;
	} = {}) => {
		if (busy.current || !quote) return;
		busy.current = true;
		setRefusal(null);
		setStep('processing');
		let stage: 'confirm' | 'payment' = 'confirm';
		if (timing === 'renewal') pendingChange.current = null;
		try {
			const change =
				pendingChange.current ??
				(await requestPlanChange({
					subscriptionId,
					toProductVersionId: target.productVersionId,
					channel: getIsNative() ? 'app' : 'web',
					expectedDiffCents: quote.diff_cents,
					timing,
				}));
			if (change.status === 'applied' || change.status === 'scheduled') {
				finish(change, change.status, timing);
				return;
			}
			if (change.status !== 'pending_payment' || !change.proration_cart_id || !tokenId) {
				refuse(planChangeRefusalOf(null), stage);
				return;
			}
			pendingChange.current = change;
			stage = 'payment';
			const paymentRefusal = await payAdjustment(change, tokenId);
			if (paymentRefusal) {
				refuse(paymentRefusal, stage);
				return;
			}
			const applied = await waitUntilApplied(change.plan_change_id);
			finish(change, applied ? 'applied' : 'paid_pending', timing);
		} catch (error) {
			refuse(planChangeRefusalOf(error), stage);
		} finally {
			busy.current = false;
		}
	};

	/** Back to the quote after a refusal: a fresh quote when the old one is no longer valid. */
	const review = () => {
		if (quoteQuery.isError || refusal?.next === 'requote') quoteQuery.refetch();
		setRefusal(null);
		setStep('review');
	};

	return {
		step,
		quote,
		isQuoting: quoteQuery.isFetching,
		quoteRefusal,
		refusal,
		done,
		confirm,
		review,
	};
};
